
import { integrateDatePatterns } from './date-patterns.js';
import { debugMode } from './filter-logic-config.js';
import { parseDateFromQuery } from './query-parser.js';
import { applyPrefilters } from './file-prefilter.js';
import { runSearch } from './search-executor.js';
import { applyFuzzyRerank } from './fuzzy-rerank.js';
import { handleFuzzyFallback, attachFallbackListener } from './fallback-handler.js';
import { updateResultsDisplay, logFinalResults } from './display-updater.js';
import { updateFuseIcon } from './fuse-icon.js';

// Used to detect when the user query changed between calls (gates verbose logs)
let lastLoggedQuery = null;

/**
 * Apply filters to files - main filtering logic.
 * Public contract preserved: imported by manager-core.js as the only consumer.
 * @param {FilterManager} filterManager - FilterManager instance
 * @param {boolean} updateUrl - Whether to update URL in browser history
 */
export function applyFiltersLogic(filterManager, updateUrl = true) {
    const uiFilters = readUiFilters(filterManager);
    filterManager.activeFilters = { ...filterManager.activeFilters, ...uiFilters };

    let files = (window.filesStore && typeof window.filesStore.getFiles === 'function') ? window.filesStore.getFiles() : [];
    if (!files.length) {
        if (debugMode === '1') console.warn('[FilterManager] No files to filter');
        filterManager.notifyFilterChange();
        return;
    }

    const queryOriginal = filterManager.activeFilters.search || '';
    const { query, dateFromQuery, dateToQuery, dateVariants, usedTokens, recognized } = parseDateFromQuery(queryOriginal);

    // Allow short tokens (e.g. "2", "58") only when there's a date context, otherwise they're noise
    const hasAnyDate = !!dateFromQuery || !!dateToQuery || !!dateVariants
        || (typeof uiFilters.dateFrom === 'string' && uiFilters.dateFrom.trim())
        || (typeof uiFilters.dateTo === 'string' && uiFilters.dateTo.trim());
    try { window.__ALLOW_SHORT_TOKENS__ = !!hasAnyDate; } catch(_) {}

    const queryChanged = queryOriginal !== lastLoggedQuery;
    if (debugMode === '1' && queryOriginal && queryChanged) {
        console.log('[SEARCH] User query:', queryOriginal);
        lastLoggedQuery = queryOriginal;
    }

    const queryForMatchConditions = queryOriginal || query;
    let conditions = (typeof window.getMatchConditions === 'function') ? window.getMatchConditions(queryForMatchConditions) : [];
    conditions = integrateDatePatterns(conditions, queryOriginal, dateFromQuery, dateToQuery, dateVariants, usedTokens, recognized);

    files = applyPrefilters(files, filterManager.activeFilters, uiFilters, queryChanged);
    const filesBeforeSearch = files.length;

    const matches = runSearch({
        inputList: files,
        conditions,
        query,
        queryOriginal,
        queryChanged,
        filesBeforeSearch
    });

    const candidates = Array.isArray(matches) ? matches : [];
    let { output, fuseWasUsed } = applyFuzzyRerank({ candidates, query, filterManager });

    ({ output, fuseWasUsed } = handleFuzzyFallback({ output, query, files, filterManager, fuseWasUsed }));

    updateResultsDisplay(output, queryChanged);
    logFinalResults(output, queryOriginal, queryChanged);

    if (window.searchUI) window.searchUI.updateSearchStatus(matches.length, query);
    filterManager.notifyFilterChange();

    updateFuseIcon(fuseWasUsed);

    if (updateUrl && window.filterController && typeof window.filterController.updateUrlWithFilters === 'function') {
        window.filterController.updateUrlWithFilters(true);
    }

    attachFallbackListener(filterManager);
}

function readUiFilters(filterManager) {
    let searchValue = (filterManager.filterElements.searchInput && filterManager.filterElements.searchInput.value) || '';
    try {
        const $sb = window.jQuery ? window.jQuery('#fnd_searchbox') : null;
        if ($sb && typeof $sb.typeahead === 'function') {
            const taVal = $sb.typeahead('val');
            if (typeof taVal === 'string' && taVal.length >= 0) searchValue = taVal;
        }
    } catch(_) {}

    const publicOnlyCheckbox = document.getElementById('public-only-filter');
    const publicOnly = publicOnlyCheckbox && publicOnlyCheckbox.checked;

    // Read date filters from filterController.activeFilters first (most up-to-date),
    // fallback to filterManager.activeFilters, then to input elements
    const dateFromController = (window.filterController && window.filterController.activeFilters && window.filterController.activeFilters.dateFrom) || '';
    const dateToController = (window.filterController && window.filterController.activeFilters && window.filterController.activeFilters.dateTo) || '';
    const dateFromManager = (filterManager.activeFilters && filterManager.activeFilters.dateFrom) || '';
    const dateToManager = (filterManager.activeFilters && filterManager.activeFilters.dateTo) || '';
    const dateFromInput = (filterManager.filterElements.dateFromInput && filterManager.filterElements.dateFromInput.value) || '';
    const dateToInput = (filterManager.filterElements.dateToInput && filterManager.filterElements.dateToInput.value) || '';

    return {
        dateFrom: dateFromController || dateFromManager || dateFromInput,
        dateTo: dateToController || dateToManager || dateToInput,
        search: searchValue,
        subcategory: (filterManager.filterElements.subcategorySelect && filterManager.filterElements.subcategorySelect.value) || null,
        format: (filterManager.filterElements.formatFilter && filterManager.filterElements.formatFilter.value) || null,
        publicOnly: publicOnly
    };
}
